import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";

const LIKERT = [
  { v: 1, lbl: "Nunca" },
  { v: 2, lbl: "Raramente" },
  { v: 3, lbl: "Às vezes" },
  { v: 4, lbl: "Frequentemente" },
  { v: 5, lbl: "Sempre" },
  { v: 0, lbl: "NA" },
];

/**
 * OCR import modal — 3 stages:
 *   1) upload (file picker)
 *   2) extracting (loader)
 *   3) review (table editable, then submit)
 */
export default function OcrImportModal({ assessmentId, testType, onClose, onImported }) {
  const [stage, setStage] = useState("upload"); // upload | extracting | review
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState(null);   // { confidence, notes, model, answered_count }
  const [answers, setAnswers] = useState({});   // { q1: 3, ... }
  const [emp, setEmp] = useState({ name: "", matricula: "" });
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const totalQuestions = questions.length || 0;

  useEffect(() => {
    // Carrega o banco de perguntas do instrumento correto (legado ou novo).
    // testType=undefined/'nr01_54' → /questions (legado); demais → /questions/{type}
    const tt = testType || "nr01_54";
    const endpoint = tt === "nr01_54" ? "/questions" : `/questions/${tt}`;
    api.get(endpoint).then(({ data }) => setQuestions(data.questions));
  }, [testType]);

  const onFile = (f) => {
    if (!f) return;
    const ok = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/jpg"];
    if (!ok.includes(f.type)) {
      toast.error("Use PDF, JPG, PNG ou WebP.");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("Arquivo maior que 15MB.");
      return;
    }
    setFile(f);
  };

  const extract = async () => {
    if (!file) return;
    setStage("extracting");
    setError("");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/assessments/${assessmentId}/ocr-extract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      const map = {};
      data.answers.forEach((a) => { map[a.question_id] = a.value; });
      setAnswers(map);
      setMeta({
        confidence: data.confidence,
        notes: data.notes,
        model: data.model,
        answered_count: data.answered_count,
        extracted_count: data.extracted_count,
      });
      setStage("review");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setError(typeof detail === "string" ? detail : JSON.stringify(detail));
      setStage("upload");
    }
  };

  const submit = async () => {
    const list = Object.entries(answers).map(([qid, v]) => ({ question_id: qid, value: Number(v) }));
    if (list.length === 0) {
      toast.info("Marque ao menos uma resposta.");
      return;
    }
    try {
      await api.post(`/assessments/${assessmentId}/ocr-import`, {
        employee_name: emp.name,
        employee_matricula: emp.matricula,
        answers: list,
        source: "ocr",
      });
      toast.success("Resposta importada com sucesso!");
      onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white border w-full max-w-5xl max-h-[92vh] flex flex-col" data-testid="ocr-modal">
        <div className="p-6 border-b border-slate-200 flex justify-between items-start">
          <div>
            <div className="overline mb-1">Importar questionário digitalizado</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight">Leitura por IA Visão</h2>
            <p className="text-sm text-slate-600 mt-1">
              Faça upload do PDF/imagem escaneado. A IA extrai as marcações e você revisa antes de salvar.
            </p>
          </div>
          <button onClick={onClose} className="btn btn-ghost text-sm" data-testid="ocr-close">
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {stage === "upload" && (
            <div>
              <input ref={inputRef} type="file" accept="application/pdf,image/*"
                     className="hidden" onChange={(e) => onFile(e.target.files?.[0])}
                     data-testid="ocr-file-input" />
              <button onClick={() => inputRef.current?.click()}
                      className="w-full border-2 border-dashed border-slate-300 hover:border-emerald-900 p-12 text-center transition-colors bg-slate-50 hover:bg-emerald-50"
                      data-testid="ocr-drop-zone">
                <i className="fa-solid fa-file-arrow-up text-4xl text-emerald-900 mb-3"></i>
                <div className="font-display text-xl font-extrabold text-slate-900">
                  {file ? file.name : "Selecionar arquivo"}
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB · ${file.type}` : "PDF, JPG, PNG ou WebP · até 15MB"}
                </div>
              </button>

              {error && (
                <div className="mt-4 p-3 border border-red-200 bg-red-50 text-sm text-red-800">
                  <i className="fa-solid fa-triangle-exclamation mr-2"></i> {error}
                </div>
              )}

              <div className="mt-4 p-3 border border-slate-200 bg-slate-50 text-xs text-slate-600">
                <strong>Como funciona:</strong> a IA analisa o documento, identifica qual alternativa
                (1-5 ou NA) foi marcada em cada uma das {totalQuestions || "—"} perguntas do instrumento e devolve para você revisar.
                Você confere, ajusta o que precisar e salva como uma resposta normal.
              </div>
            </div>
          )}

          {stage === "extracting" && (
            <div className="text-center py-20">
              <i className="fa-solid fa-circle-notch fa-spin text-3xl text-emerald-900 mb-4"></i>
              <div className="font-display text-xl font-extrabold text-slate-900">Lendo o documento…</div>
              <div className="text-sm text-slate-500 mt-2">Isso pode levar 15-40 segundos. Não feche esta janela.</div>
            </div>
          )}

          {stage === "review" && (
            <div>
              {/* Meta info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="border border-slate-200 p-3">
                  <div className="overline">Detectadas</div>
                  <div className="kpi-number text-2xl text-emerald-900 mt-1">{meta.answered_count}/{totalQuestions || meta.expected_count || meta.answered_count}</div>
                </div>
                <div className="border border-slate-200 p-3">
                  <div className="overline">Confiança IA</div>
                  <div className="kpi-number text-2xl text-emerald-900 mt-1">
                    {Math.round((meta.confidence || 0) * 100)}%
                  </div>
                </div>
                <div className="border border-slate-200 p-3">
                  <div className="overline">Modelo</div>
                  <div className="text-xs font-mono mt-2 text-slate-700">{meta.model}</div>
                </div>
                <div className="border border-slate-200 p-3">
                  <div className="overline">Notas</div>
                  <div className="text-xs text-slate-700 mt-1 line-clamp-2">{meta.notes || "—"}</div>
                </div>
              </div>

              {/* Identification */}
              <div className="grid md:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="label">Nome (opcional)</label>
                  <input value={emp.name} onChange={(e) => setEmp({ ...emp, name: e.target.value })}
                         className="input" data-testid="ocr-name-input" />
                </div>
                <div>
                  <label className="label">Matrícula (opcional)</label>
                  <input value={emp.matricula} onChange={(e) => setEmp({ ...emp, matricula: e.target.value })}
                         className="input" data-testid="ocr-matricula-input" />
                </div>
              </div>

              {/* Editable answers table */}
              <div className="overline mb-2">Revise as respostas extraídas</div>
              <div className="border border-slate-200 max-h-[420px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-2 font-bold uppercase w-10">#</th>
                      <th className="text-left p-2 font-bold uppercase">Pergunta</th>
                      <th className="text-center p-2 font-bold uppercase w-72">Resposta detectada</th>
                    </tr>
                  </thead>
                  <tbody>
                    {questions.map((q) => (
                      <tr key={q.id} className="border-b border-slate-100" data-testid={`ocr-row-${q.id}`}>
                        <td className="p-2 font-mono text-slate-500">{q.numero}</td>
                        <td className="p-2 text-slate-800">{q.pergunta}</td>
                        <td className="p-2">
                          <div className="flex gap-1 justify-center">
                            {LIKERT.map((opt) => {
                              const sel = answers[q.id] === opt.v;
                              return (
                                <button key={opt.v} type="button"
                                        onClick={() => setAnswers({ ...answers, [q.id]: opt.v })}
                                        title={opt.lbl}
                                        data-testid={`ocr-${q.id}-${opt.v}`}
                                        className={`w-7 h-7 border text-xs font-bold transition-all ${
                                          sel
                                            ? "bg-emerald-900 text-white border-emerald-900"
                                            : "bg-white text-slate-700 border-slate-300 hover:border-emerald-900"
                                        }`}>
                                  {opt.v === 0 ? "NA" : opt.v}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-slate-200 flex justify-between items-center gap-2">
          <div className="text-xs text-slate-500">
            {stage === "review" && `${Object.values(answers).filter((v) => v !== undefined).length} resposta(s) prontas para salvar`}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn btn-secondary" data-testid="ocr-cancel">Cancelar</button>
            {stage === "upload" && (
              <button onClick={extract} disabled={!file} className="btn btn-primary" data-testid="ocr-extract-button">
                <i className="fa-solid fa-wand-magic-sparkles"></i> Analisar com IA
              </button>
            )}
            {stage === "review" && (
              <button onClick={submit} className="btn btn-primary" data-testid="ocr-import-button">
                <i className="fa-solid fa-check"></i> Importar como resposta
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
